import React, { useState } from 'react';
import './Chatbot360.scss';
import BlogomatPopupForm from '../components/BlogomatPopupForm';
import DecoIco1 from '../images/svg/decoIco1.svg';
import DecoIco2 from '../images/svg/decoIco2.svg';
import DecoIco3 from '../images/svg/decoIco3.svg';

function Chatbot360() {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const chatbotPackages = [
    { value: 'Flexible Package', label: 'Flexible Package — no contract' },
    { value: 'Partnership Package', label: 'Partnership Package — 12-month contract' },
  ];

  return (
    <div className="chatbot360-page">
      {/* Hero Section */}
      <div className="chatbot360-hero">
        <div className="chatbot360-hero-content">
          <h1>CHATBOT 360°</h1>
          <p className="chatbot360-tagline">
            AI that finally handles customer questions, leads, and your company's knowledge — instead of another annoying popup 💬.
          </p>
        </div>
      </div>

      {/* How it works */}
      <div className="chatbot360-how-it-works">
        <h2 className="section-title">HOW DOES CHATBOT 360° WORK?</h2>
        <div className="how-content">
          <p className="intro-text">
            Chatbot 360° is your online "employee" — works 24/7, even when no one is in the office. A customer asks a question and immediately gets an answer:
          </p>
          <ul className="feature-list">
            <li>• chatbot searches for information on the website,</li>
            <li>• suggests the right pages,</li>
            <li>• and when needed — recommends products or services tailored to needs.</li>
          </ul>
          <p className="lead-info">
            Additionally, it collects leads: it can ask for contact information and briefly describe the topic, so you get ready inquiries instead of "please quote" without details.
          </p>
        </div>
      </div>

      {/* Problem Section */}
      <div className="chatbot360-problem">
        <h2 className="section-title">THE PROBLEM MOST COMPANIES DON'T EVEN NOTICE</h2>
        <div className="problem-content">
          <p className="problem-intro">In many companies it looks similar:</p>
          <div className="problem-grid">
            <div className="problem-card">
              <p>• There's a contact form and phone number on the website.</p>
            </div>
            <div className="problem-card">
              <p>• There's an FAQ, but few people look at it.</p>
            </div>
            <div className="problem-card">
              <p>• Customers send emails with the same questions.</p>
            </div>
            <div className="problem-card">
              <p>• The team spends a lot of time on "copy-paste" replies.</p>
            </div>
            <div className="problem-card">
              <p>• Users who don't have time to search just close the tab.</p>
            </div>
          </div>
          <p className="problem-conclusion">
            This is not a problem with your sales people. <strong>This is a problem of lacking an intelligent 24/7 assistant on the website.</strong>
          </p>
        </div>
      </div>

      {/* What Chatbot Does */}
      <div className="chatbot360-what-it-does">
        <h2 className="section-title">WHAT CHATBOT 360° DOES – IN SHORT</h2>
        <p className="section-intro">
          Chatbot 360° is an AI layer between your website and the user, which:
        </p>
        <div className="steps-grid">
          <div className="step-card">
            <div className="step-number">1</div>
            <h3>Collects knowledge about your company</h3>
            <p>We train the bot based on: website content, documents (PDF, DOC, presentations), FAQ, procedures, offers, terms and conditions, and other materials you provide.</p>
          </div>
          <div className="step-card">
            <div className="step-number">2</div>
            <h3>Builds a knowledge base (RAG)</h3>
            <p>We create a structure of questions and answers based on your data — so the chatbot doesn't make things up, but draws on facts from sources you provide.</p>
          </div>
          <div className="step-card">
            <div className="step-number">3</div>
            <h3>Converses with customers on the website</h3>
            <p>We embed Chatbot 360° as a widget on the website (e.g., in the bottom right corner). Users write like on Messenger — the chatbot responds in a tone matched to your brand.</p>
          </div>
          <div className="step-card">
            <div className="step-number">4</div>
            <h3>Guides the user to the goal</h3>
            <p>The chatbot suggests appropriate pages/offers, helps select a service/product, and explains the cooperation process, timelines, and rules.</p>
          </div>
          <div className="step-card">
            <div className="step-number">5</div>
            <h3>Collects leads at the right moment</h3>
            <p>When we see that the conversation is "hot," the chatbot politely asks for: name, email, phone, and additional information (e.g., budget, scope, city).</p>
          </div>
          <div className="step-card">
            <div className="step-number">6</div>
            <h3>Sends data to you</h3>
            <p>Leads can go to: email, CRM, spreadsheet / n8n / other system — according to arrangements. You get ready inquiries, not just "clicks" to the website.</p>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div className="chatbot360-benefits">
        <h2 className="section-title">WHAT YOU GET WITH CHATBOT 360°</h2>
        <div className="benefits-grid">
          <div className="benefit-card">
            <h4>🕰️ 24/7 Answers</h4>
            <p>Customer gets help even when no one is in the office.</p>
          </div>
          <div className="benefit-card">
            <h4>📧 Fewer repetitive messages</h4>
            <p>Chatbot takes over questions about offers, prices, deadlines, delivery, and "how to order."</p>
          </div>
          <div className="benefit-card">
            <h4>🔍 Faster information finding</h4>
            <p>Bot provides specifics and directs exactly where needed.</p>
          </div>
          <div className="benefit-card">
            <h4>🎯 Better offer matching</h4>
            <p>Can recommend a product/service based on customer needs.</p>
          </div>
          <div className="benefit-card">
            <h4>📈 More leads</h4>
            <p>Collects contact and inquiry context, so you get ready, sensible submissions.</p>
          </div>
          <div className="benefit-card">
            <h4>📁 Organized knowledge</h4>
            <p>Your materials (website, PDFs, FAQ) work as one answer base.</p>
          </div>
        </div>
        <div className='deco-icons-chatbot'>
          <img src={DecoIco1} alt='decoration icon' className='deco-icon deco-icon1' />
          <img src={DecoIco2} alt='decoration icon' className='deco-icon deco-icon2' />
          <img src={DecoIco3} alt='decoration icon' className='deco-icon deco-icon3' />
        </div>
      </div>

      {/* For Whom */}
      <div className="chatbot360-for-whom">
        <h2 className="section-title">WHO IS CHATBOT 360° FOR</h2>
        <div className="for-whom-content">
          <p className="for-whom-intro">
            Chatbot 360° is for companies that have traffic on their website and don't want that traffic to leave without answers. It works especially well when the offer is extensive and customer questions often repeat.
          </p>
          <div className="use-cases">
            <div className="use-case-card">
              <h4>🛒 E-commerce stores</h4>
              <p>Suggests products, helps choose variants, answers about delivery, returns, and payments.</p>
            </div>
            <div className="use-case-card">
              <h4>💼 Service companies</h4>
              <p>Explains service scope, prices, timelines, directs to the right service, and collects contact for quotes.</p>
            </div>
            <div className="use-case-card">
              <h4>🏢 B2B with longer purchase process</h4>
              <p>Pre-qualifies inquiries and collects specific information (instead of "please quote").</p>
            </div>
            <div className="use-case-card">
              <h4>📚 Companies with large knowledge base</h4>
              <p>Catalogs, PDFs, instructions, regulations, specifications — chatbot quickly finds the right answer.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="chatbot360-pricing">
        <h2 className="section-title">PACKAGES & PRICING</h2>
        <p className="pricing-intro">
          For the convenience of our clients, we've prepared two clear collaboration packages — you can choose an option without obligations or a subscription model with a 12-month contract.
        </p>
        <div className="pricing-grid">
          <div className="pricing-card">
            <h3>FLEXIBLE PACKAGE</h3>
            <p className="package-subtitle">(no contract)</p>
            <div className="package-price">
              <div className="price-item">
                <span className="price-label">Implementation: </span>
                <span className="price-value"> 1200 €</span>
              </div>
              <div className="price-item">
                <span className="price-label">Subscription:</span>
                <span className="price-value"> 100 € / month</span>
              </div>
            </div>
            <ul className="package-features">
              <li>✓ Chatbot 360 configuration + embedding on website</li>
              <li>✓ Learning from your data (website + materials)</li>
              <li>✓ Setting paths: questions → answer → link to page</li>
              <li>✓ Lead collection (contact + inquiry topic)</li>
              <li>✓ Quality testing + launch</li>
            </ul>
          </div>
          
          <div className="pricing-card featured">
            <div className="featured-badge">RECOMMENDED</div>
            <h3>PARTNERSHIP PACKAGE</h3>
            <p className="package-subtitle">(12-month contract)</p>
            <div className="package-price">
              <div className="price-item">
                <span className="price-label">Implementation:</span>
                <span className="price-value">Free</span>
              </div>
              <div className="price-item">
                <span className="price-label">Subscription:</span>
                <span className="price-value"> 200 € / month</span>
              </div>
            </div>
            <ul className="package-features">
              <li>✓ Chatbot 360 configuration + embedding on website</li>
              <li>✓ Learning from your data (website + materials)</li>
              <li>✓ Setting paths: questions → answer → link to page</li>
              <li>✓ Lead collection (contact + inquiry topic)</li>
              <li>✓ Quality testing + launch</li>
              <li>✓ Free implementation (no upfront cost)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="chatbot360-cta">
        <h2>🚀 Launch Chatbot 360° on your website</h2>
        <p>Chatbot 360° learns from your website and materials, so it answers customer questions, helps find information, recommends products/services, and collects leads — 24/7.</p>
        <button className="blue-button2 cta-btn" onClick={() => setIsPopupOpen(true)}>Get Started</button>
      </div>

      <BlogomatPopupForm
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        title='Get Started with Chatbot 360°'
        packages={chatbotPackages}
      />
    </div>
  );
}

export default Chatbot360;
